// V5.1 REFONDATION : Composant "Mes Missions en Cours"
// V5.8 VERROUILLAGE POST-SIGNATURE : has_mandat est la source de vérité
// V5.48 : Garde-fou pack_type - Isolation des silos Succession/Audit
// V5.66 : 5 statuts officiels miroir Admin → Client (Lancement/Traitement/Expédition/Résiliation/Effacé)
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, FileText, Scan, ArrowRight, CheckCircle, Clock,
  AlertCircle, Loader2, Plus, Settings, Shield, Lock, Wrench,
  Send, Truck, Save
} from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';

// Configuration des types de mission
const MISSION_TYPE_CONFIG = {
  demenagement: {
    label: 'Mission Déménagement',
    icon: Home,
    color: 'cyan',
    path: '/missions/demenagement',
    implemented: true
  },
  succession: {
    label: 'Mission Héritage',
    icon: FileText,
    color: 'magenta',
    path: '/missions/succession',
    implemented: true
  },
  audit: {
    label: 'Audit Pouvoir d\'Achat',
    icon: Scan,
    color: 'success',
    path: '/dashboard?tab=audit',
    implemented: false
  }
};

// Mapping des pack_type vers mission_type
const PACK_TO_TYPE = {
  'mission_demenagement': 'demenagement',
  'nouveau_depart': 'demenagement',
  'demenagement': 'demenagement',
  'mission_heritage': 'succession',
  'serenite_succession': 'succession',
  'mission_audit': 'audit',
  'audit_pouvoir_achat': 'audit'
};

// V5.66 : 5 STATUTS OFFICIELS — Télécommande Admin → Barre Client
const STATUS_CONFIG = {
  // === 5 STATUTS OFFICIELS ===
  nouveau: { 
    label: 'Lancement', 
    clientLabel: 'Dossier lancé — Expert en charge',
    color: 'cyan', 
    icon: Clock,
    step: 1
  },
  en_cours_de_traitement: { 
    label: 'Traitement', 
    clientLabel: 'Dossier en cours de traitement',
    color: 'blue', 
    icon: Loader2,
    step: 2
  },
  resiliation_envoyee: { 
    label: 'Expédition', 
    clientLabel: 'Mandats expédiés aux fournisseurs',
    color: 'purple', 
    icon: Send,
    step: 3
  },
  validation_resiliation: { 
    label: 'Résiliation', 
    clientLabel: 'Retours fournisseurs en cours',
    color: 'orange', 
    icon: Truck,
    step: 4
  },
  termine: { 
    label: 'Effacé', 
    clientLabel: 'Mission accomplie — Succès total',
    color: 'success', 
    icon: CheckCircle,
    step: 5
  },
  // === STATUTS HÉRITÉS (compatibilité) ===
  // DDD.7 : statut "brouillon" = tunnel sauvegardé en cours, le client peut le reprendre
  brouillon: { label: 'À reprendre', clientLabel: 'Tunnel sauvegardé — à compléter', color: 'cyan', icon: Save, step: 1 },
  details_submitted: { label: 'Lancement', clientLabel: 'Dossier lancé', color: 'cyan', icon: Clock, step: 1 },
  en_attente_documents: { label: 'Lancement', clientLabel: 'Dossier lancé', color: 'cyan', icon: Clock, step: 1 },
  en_cours: { label: 'Traitement', clientLabel: 'Dossier en traitement', color: 'blue', icon: Loader2, step: 2 },
  pending: { label: 'Lancement', clientLabel: 'Dossier lancé', color: 'cyan', icon: Clock, step: 1 },
  BROUILLON: { label: 'Lancement', clientLabel: 'Dossier lancé', color: 'gray', icon: Clock, step: 1 },
  SIGNE: { label: 'Lancement', clientLabel: 'Mandat signé', color: 'success', icon: Shield, step: 1 },
  EN_COURS: { label: 'Traitement', clientLabel: 'Dossier en traitement', color: 'blue', icon: Loader2, step: 2 },
  traitement: { label: 'Traitement', clientLabel: 'Dossier en traitement', color: 'orange', icon: Loader2, step: 2 },
  TRAITEMENT: { label: 'Traitement', clientLabel: 'Dossier en traitement', color: 'orange', icon: Loader2, step: 2 },
  TERMINE: { label: 'Effacé', clientLabel: 'Mission accomplie', color: 'success', icon: CheckCircle, step: 5 },
  VALIDATED: { label: 'Lancement', clientLabel: 'Dossier validé', color: 'success', icon: CheckCircle, step: 1 },
  annule: { label: 'Annulé', clientLabel: 'Dossier annulé', color: 'red', icon: AlertCircle, step: 0 },
  erreur: { label: 'Erreur', clientLabel: 'Erreur', color: 'red', icon: AlertCircle, step: 0 }
};

// ===================================================================
// V5.66 : BARRE DE PROGRESSION MIROIR — 5 ÉTAPES OFFICIELLES
// Lancement → Traitement → Expédition → Résiliation → Effacé
// ===================================================================
const PROGRESS_STEPS = [
  { id: 1, label: 'Lancement', color: 'cyan' },
  { id: 2, label: 'Traitement', color: 'blue' },
  { id: 3, label: 'Expédition', color: 'purple' },
  { id: 4, label: 'Résiliation', color: 'orange' },
  { id: 5, label: 'Effacé', color: 'green' }
];

const getStepFromStatus = (status) => {
  const config = STATUS_CONFIG[status];
  return config?.step || 1;
};

const MissionProgressBar = ({ status }) => {
  const currentStep = getStepFromStatus(status);
  const isCancelled = status === 'annule';

  if (isCancelled) {
    return (
      <div className="mb-4 p-2 bg-red-400/10 border border-red-400/30 rounded-lg text-center">
        <span className="text-red-400 text-xs font-mono">DOSSIER ANNULÉ</span>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between relative">
        {/* Ligne de fond */}
        <div className="absolute top-3 left-4 right-4 h-0.5 bg-gray-700" />
        {/* Ligne de progression */}
        <div 
          className="absolute top-3 left-4 h-0.5 bg-cyan-400 transition-all duration-700"
          style={{ width: `${Math.max(0, ((currentStep - 1) / 4) * 100)}%` }}
        />
        {PROGRESS_STEPS.map((step) => {
          const isPast = step.id < currentStep;
          const isActive = step.id === currentStep;
          return (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                isPast ? 'bg-cyan-400 text-black' :
                isActive ? 'bg-cyan-400/30 border-2 border-cyan-400 text-cyan-400 animate-pulse' :
                'bg-gray-700 text-gray-500'
              }`}>
                {isPast ? <CheckCircle className="w-3.5 h-3.5" /> : step.id}
              </div>
              <span className={`text-[10px] mt-1 font-mono ${
                isPast || isActive ? 'text-cyan-400' : 'text-gray-600'
              }`}>{step.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MesMissionsEnCours = ({ onMissionSelect }) => {
  const [missions, setMissions] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);

  // Charger les missions actives + email de l'utilisateur
  useEffect(() => {
    const loadMissions = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        const [missionsRes, userRes] = await Promise.all([
          axios.get(`${API}/missions/active`, { headers, withCredentials: true }),
          axios.get(`${API}/auth/me`, { headers, withCredentials: true }).catch(() => ({ data: { user: {} } }))
        ]);
        setMissions(missionsRes.data.missions || []);
        setUserEmail(userRes.data?.user?.email || userRes.data?.email || '');
      } catch (error) {
        console.error('Erreur chargement missions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMissions();
  }, []);

  if (loading) {
    return (
      <div className="glass-card p-6 border border-gray-700">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (missions.length === 0) {
    return (
      <div className="glass-card p-6 border border-gray-700">
        <h2 className="font-orbitron text-xl font-bold text-white mb-4">
          Mes Missions en Cours
        </h2>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-800 rounded-full flex items-center justify-center">
            <Plus className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-400">Aucune mission active pour le moment</p>
          <p className="text-gray-500 text-sm mt-2">Retournez à l'accueil pour démarrer une nouvelle mission</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl font-bold text-white">Mes Missions en Cours</h2>
        <span className="px-2 py-1 bg-cyan-400/20 text-cyan-400 text-sm font-mono rounded">
          {missions.length} active(s)
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <AnimatePresence>
          {missions.map((mission, idx) => {
            const missionType = PACK_TO_TYPE[mission.pack_type] || 'demenagement';
            const config = MISSION_TYPE_CONFIG[missionType];
            const Icon = config.icon;

            // Verrouillage post-signature
            const isValidated = mission.status === 'VALIDATED' || mission.mission_validated === true;
            const hasSignedMandat = mission.has_mandat === true || isValidated;

            // V5.66 : SYNCHRONISATION MIROIR — statut admin pilote tout
            const adminStatus = mission.status || 'nouveau';
            const statusConfig = STATUS_CONFIG[adminStatus] || STATUS_CONFIG.nouveau;
            const StatusIcon = statusConfig.icon;

            // V1.2 : tunnel Héritage maintenant opérationnel — plus de blocage spécifique succession.
            // On bloque uniquement les tunnels réellement non implémentés (ex: audit sans mandat signé).
            const isHeritage = missionType === 'succession';
            const isTunnelImplemented = config.implemented === true;
            const isBlocked = !isTunnelImplemented && !hasSignedMandat;

            // Routing du bouton
            const linkDestination = isBlocked
              ? null
              : hasSignedMandat
                ? `/missions/manage?mission=${mission.mission_id}`
                : `${config.path}${mission.mission_id ? `?mission=${mission.mission_id}` : ''}`;

            // DDD.7 : "Reprendre — étape X/N" quand mission en brouillon (current_step renseigné).
            const totalSteps = missionType === 'audit' ? 3 : 4;
            const currentStepNum = mission.details?.current_step
              || mission.current_step
              || mission.details?.mission_data?.current_step
              || 1;
            const isDraft = (mission.status === 'brouillon' || mission.details?.status === 'brouillon')
              || (!hasSignedMandat && currentStepNum > 1);
            const stepProgress = isDraft && !hasSignedMandat ? ` — étape ${Math.min(currentStepNum, totalSteps)}/${totalSteps}` : '';

            const buttonText = isBlocked
              ? 'En cours de configuration'
              : hasSignedMandat
                ? 'Gérer mon dossier'
                : (isDraft ? `Reprendre${stepProgress}` : 'Continuer');
            const ButtonIcon = isBlocked ? Wrench : hasSignedMandat ? Settings : ArrowRight;

            // V5.66 ACTION 2 : Fallback email — mission_data.client.email → auth.user.email
            const clientEmail = mission.mission_data?.client?.email
              || mission.details?.form_data?.email
              || userEmail
              || '';

            return (
              <motion.div
                key={mission.mission_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: idx * 0.1 }}
                className={`glass-card p-4 border ${hasSignedMandat ? 'border-success/50 bg-success/5' : `border-${config.color}-400/30`} hover:border-${hasSignedMandat ? 'success' : config.color}-400/60 transition-all group`}
                data-testid={`mission-card-${mission.mission_id}`}
              >
                {/* Header : icône + nom + statut */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${hasSignedMandat ? 'bg-success/20' : `bg-${config.color}-400/20`} flex items-center justify-center`}>
                      {hasSignedMandat ? (
                        <Shield className="w-5 h-5 text-success" />
                      ) : (
                        <Icon className={`w-5 h-5 text-${config.color}-400`} />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{config.label}</p>
                      <p className="text-gray-500 text-xs font-mono">{mission.display_id || mission.mission_id?.slice(0, 15) || 'N/A'}</p>
                    </div>
                  </div>
                  {/* Badge statut */}
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-mono ${hasSignedMandat ? 'bg-success/20 text-success' : 'bg-cyan-400/20 text-cyan-400'}`}>
                    <StatusIcon className={`w-3 h-3 ${StatusIcon === Loader2 ? 'animate-spin' : ''}`} />
                    {statusConfig.label}
                  </div>
                </div>

                {/* Statut client en une ligne */}
                <p className="text-gray-400 text-sm mb-4 pl-1">{statusConfig.clientLabel}</p>

                {/* DDD.7 : mini barre de progression — visible uniquement pour les brouillons reprenables */}
                {!hasSignedMandat && !isBlocked && isDraft && (
                  <div className="mb-3 px-1">
                    <div className="flex items-center justify-between text-[10px] font-mono text-gray-500 mb-1.5">
                      <span>// PROGRESSION</span>
                      <span>{Math.min(currentStepNum, totalSteps)}/{totalSteps}</span>
                    </div>
                    <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r from-${config.color}-400 to-${config.color}-300 transition-all`}
                        style={{ width: `${Math.min((currentStepNum / totalSteps) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Footer : date + bouton */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-xs">
                    {mission.updated_at ? new Date(mission.updated_at).toLocaleDateString('fr-FR') : 'N/A'}
                  </span>

                  {isBlocked ? (
                    <Button
                      size="sm"
                      disabled
                      className="bg-gray-700 text-gray-400 cursor-not-allowed text-xs"
                      data-testid={`mission-blocked-btn-${mission.mission_id}`}
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      En configuration
                    </Button>
                  ) : (
                    <Link to={linkDestination} data-testid={`mission-action-btn-${mission.mission_id}`}>
                      <Button
                        size="sm"
                        className={`${hasSignedMandat
                          ? 'bg-success text-black hover:bg-success/90'
                          : `bg-${config.color}-400 text-black hover:bg-${config.color}-300`
                        } group-hover:translate-x-1 transition-transform font-semibold`}
                      >
                        {buttonText}
                        <ButtonIcon className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>

                {/* V1.2 : message "interface en config" supprimé, tunnel Héritage opérationnel */}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MesMissionsEnCours;

